"use client";

import { JSX } from "react";
import "@/app/styles/devicon-subset.css";
import { SiFramer, SiLangchain, SiOpenai } from "react-icons/si";
import { useLocale } from "@/app/components/LocaleProvider";
import Reveal from "@/app/components/Reveal";

interface Tech {
    name: string;
    slug: string;
    icon: string;
}

const deviconMap: Record<string, string> = {
    react: "devicon-react-original",
    nextjs: "devicon-nextjs-plain",
    typescript: "devicon-typescript-plain",
    tailwindcss: "devicon-tailwindcss-plain",
    python: "devicon-python-plain",
    go: "devicon-go-plain",
    java: "devicon-java-plain",
    nodejs: "devicon-nodejs-plain",
    fastapi: "devicon-fastapi-plain",
    django: "devicon-django-plain",
    postgresql: "devicon-postgresql-plain",
    mongodb: "devicon-mongodb-plain",
    redis: "devicon-redis-plain",
    elasticsearch: "devicon-elasticsearch-plain",
    reactnative: "devicon-reactnative-original",
    swift: "devicon-swift-plain",
    kotlin: "devicon-kotlin-plain",
    flutter: "devicon-flutter-plain",
    docker: "devicon-docker-plain",
    kubernetes: "devicon-kubernetes-plain",
    amazonwebservices: "devicon-amazonwebservices-plain",
    googlecloud: "devicon-googlecloud-plain",
    tensorflow: "devicon-tensorflow-original",
    pytorch: "devicon-pytorch-original",
};

const riMap: Record<string, { icon: JSX.Element; color: string }> = {
    framer: { icon: <SiFramer aria-hidden="true" />, color: "#bb4b96" },
    langchain: { icon: <SiLangchain aria-hidden="true" />, color: "#1C3C3C" },
    openai: { icon: <SiOpenai aria-hidden="true" />, color: "#10a37f" },
};

const techs: Tech[] = [
    { name: "React", slug: "react", icon: "react" },
    { name: "Next.js", slug: "nextjs", icon: "nextjs" },
    { name: "TypeScript", slug: "typescript", icon: "typescript" },
    { name: "Tailwind CSS", slug: "tailwindcss", icon: "tailwindcss" },
    { name: "Framer Motion", slug: "framer", icon: "framer" },
    { name: "Python", slug: "python", icon: "python" },
    { name: "Go", slug: "go", icon: "go" },
    { name: "Java", slug: "java", icon: "java" },
    { name: "Node.js", slug: "nodejs", icon: "nodejs" },
    { name: "FastAPI", slug: "fastapi", icon: "fastapi" },
    { name: "Django", slug: "django", icon: "django" },
    { name: "PostgreSQL", slug: "postgresql", icon: "postgresql" },
    { name: "MongoDB", slug: "mongodb", icon: "mongodb" },
    { name: "Redis", slug: "redis", icon: "redis" },
    { name: "Elasticsearch", slug: "elasticsearch", icon: "elasticsearch" },
    { name: "React Native", slug: "reactnative", icon: "reactnative" },
    { name: "Swift", slug: "swift", icon: "swift" },
    { name: "Kotlin", slug: "kotlin", icon: "kotlin" },
    { name: "Flutter", slug: "flutter", icon: "flutter" },
    { name: "Docker", slug: "docker", icon: "docker" },
    { name: "Kubernetes", slug: "kubernetes", icon: "kubernetes" },
    { name: "AWS", slug: "amazonwebservices", icon: "amazonwebservices" },
    { name: "GCP", slug: "googlecloud", icon: "googlecloud" },
    { name: "TensorFlow", slug: "tensorflow", icon: "tensorflow" },
    { name: "PyTorch", slug: "pytorch", icon: "pytorch" },
    { name: "LangChain", slug: "langchain", icon: "langchain" },
    { name: "OpenAI API", slug: "openai", icon: "openai" },
];

function TechIcon({ tech }: { tech: Tech }) {
    const devClass = deviconMap[tech.icon];
    if (devClass) {
        return (
            <i className={`${devClass} colored block text-4xl max-md:text-2xl`} />
        );
    }
    const ri = riMap[tech.icon];
    if (ri) {
        return (
            <span className="block text-4xl max-md:text-2xl" style={{ color: ri.color }}>
                {ri.icon}
            </span>
        );
    }
    return null;
}

export default function Technologies() {
    const locale = useLocale();
    const isRu = locale === "ru";

    return (
        <section className="relative bg-[#202e64] py-16 sm:py-24 max-md:py-9">
            <div className="w-full max-w-7xl mx-auto px-4">
                {/* Заголовок + описание */}
                <div className="py-6 md:py-10 max-md:py-2">
                    <Reveal y={50} duration={0.8}>
                        <h2 className="text-center text-3xl font-bold tracking-tight text-white/95 max-md:text-2xl">
                            {isRu ? "Технологии" : "Technologies"}
                        </h2>
                    </Reveal>

                    <Reveal y={50} duration={0.8}>
                        <p className="mx-auto mt-3 max-w-3xl text-center text-sm text-white/70 leading-relaxed max-md:mt-2 max-md:text-xs">
                            {isRu
                                ? "Мы используем современный и надежный технологический стек для создания высокопроизводительных, масштабируемых и безопасных приложений. Наш опыт охватывает ведущие фреймворки и платформы для предоставления решений, готовых к будущему."
                                : "We use a modern and reliable technology stack to build high-performance, scalable and secure applications. Our expertise covers leading frameworks and platforms to deliver future-ready solutions."}
                        </p>
                    </Reveal>
                </div>

                {/* Логотипы */}
                <div className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-8 max-md:mt-3 max-md:gap-x-3 max-md:gap-y-3">
                    {techs.map((tech) => (
                        <div
                            key={tech.slug}
                            className="flex w-24 flex-col items-center justify-center gap-2 text-center max-md:w-[60px] max-md:gap-1"
                        >
                            <TechIcon tech={tech} />
                            <span className="text-xs font-medium text-white/80 leading-snug max-md:text-[10px]">
                                {tech.name}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}
